import * as React from "react";
import { logger } from "bernie-core";
import { StorefrontWizardRegionCOMETProps, StorefrontWizardRegionCOMETFTNodeOptions } from "./typings";
import {
  StorefrontWizardRegionTypesContext,
  StorefrontWizardRegionTypes,
} from "components/flexComponents/PageLevelProviders/WizardContextProvider/StorefrontWizardRegionTypesContext";
import { withLocalization } from "@shared-ui/localization-context";
import { LobQueryRouter } from "src/components/shared/StorefrontWizard/LobQueryRouter";
import {
  getWizardLOBFlexComponents,
  getWizardLOBFormChildren,
  getNonWizardFormChildren,
} from "src/components/shared/StorefrontWizard/StorefrontWizardUtils";
import {
  TabbedWizardCard,
  WizardRegionType,
} from "src/components/shared/StorefrontWizard/TabbedWizardCard/TabbedWizardCard";
import { UitkFigure } from "uitk-react-images";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { CustomerNotifications } from "components/shared/CustomerNotifications";
import { withStores } from "src/stores";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { BlossomImage } from "src/components/shared/BlossomImage/BlossomImage";
import { normalizeOptionsData, hasRenderableChilren } from "src/components/flexFramework/FlexTemplateNode";
import { ExtendedFTNode } from "src/typings/flexFramework/FlexComponent";

@withLocalization
@logger("StorefrontWizardRegionCOMET")
class StorefrontWizardRegionCOMET extends React.Component<StorefrontWizardRegionCOMETProps> {
  public render() {
    const {
      flexTemplateNode,
      flexModuleModelStore,
      context,
      wizardState,
      compositionStore,
      blossomComponent,
    } = this.props;

    if (!blossomComponent) {
      return null;
    }

    const options = normalizeOptionsData(
      flexTemplateNode,
      flexModuleModelStore.getModel(flexTemplateNode.id)
    ) as StorefrontWizardRegionCOMETFTNodeOptions;

    //default to false if device information is not available
    wizardState.globalWizardState.setIsDesktop(context.deviceInformation);

    if (!options || !hasRenderableChilren(flexTemplateNode, flexModuleModelStore)) {
      return null;
    }

    wizardState.globalWizardState.config.useLastSearchForMultiLOB = options.useLastSearchForMultiLOB;

    const { defaultLOB, cdgTreatment } = options;

    if (defaultLOB) {
      wizardState.globalWizardState.config.navigation.defaultLob = defaultLOB;
      wizardState.globalWizardState.config.navigation.activeLob = defaultLOB;
    }

    wizardState.globalWizardState.setLastSearchedLob();

    const children = blossomComponent.renderChildren(this.props.logger, this.props.l10n, context);

    if (children.length < 1) {
      return null;
    }

    const wizardLOBs = getWizardLOBFlexComponents(flexTemplateNode.childNodes as ExtendedFTNode[]);
    const wizardFormChildren = getWizardLOBFormChildren(children);
    const otherFormChildren = getNonWizardFormChildren(children, flexModuleModelStore);
    const resolvedBackgroundImage =
      options.backgroundImage ||
      "https://forever.travel-assets.com/flex/flexmanager/images/2020/06/26/Orbitz-wizard-background.jpg";

    const isStorefrontPage = context.searchContext.pageType.indexOf("Storefront") > -1;
    const cometSFPadding =
      options.brandModulePadding && options.brandModulePadding === "comet_padding" ? "comet-homepage-module" : "";

    const subHeading =
      options.innerTitle || (compositionStore.composition!.pageSubHeadline && options.subTitleFromPageSubHeader);
    const heading = options.title || (compositionStore.composition!.pageHeading && options.titleFromPageHeader);

    const headingClassName = `SimpleContainer cometHeading all-x-padding-six all-t-padding-six  ${
      subHeading ? "s-b-padding-three" : "s-b-padding-six"
    }`;
    const headingElement = (isMobile: boolean) => (
      <h1 className={`${headingClassName} uitk-type-${cdgTreatment && isMobile ? "heading-600" : "display-800"}`}>
        {options.titleFromPageHeader ? compositionStore.composition!.pageHeading : options.title}
      </h1>
    );

    /**
     *
     * Note for modules below this region:
     * Modules not part of this region do not align to the form content out of the box
     * Setting the flex manager attribute brandModulePadding to comet_padding puts a spacing__twelve on the components below this region
     * This aligns them with the form content within the card on this wizard
     */
    return (
      <>
        <StorefrontWizardRegionTypesContext.Provider value={StorefrontWizardRegionTypes.STOREFRONT_WIZARD_REGION_COMET}>
          <div className={`StorefrontWizardRegionCOMET ${cometSFPadding}`} data-testid="storefront-region">
            <div data-testid="main-region" className="cometRegion">
              <UitkFigure className="cometBackgroundImageFigure">
                <BlossomImage src={resolvedBackgroundImage} alt="" />
                {cdgTreatment && <UitkScrim type={UitkScrimType.OVERLAY} />}
                {heading && (
                  <Viewport>
                    <ViewSmall>{headingElement(true)}</ViewSmall>
                    <ViewMedium>{headingElement(false)}</ViewMedium>
                  </Viewport>
                )}
                {heading && subHeading && (
                  <h2 className="SimpleContainer cometHeading uitk-type-subheading all-x-padding-six all-t-padding-three s-t-padding-zero s-b-padding-three">
                    {options.subTitleFromPageSubHeader
                      ? compositionStore.composition!.pageSubHeadline
                      : options.innerTitle}
                  </h2>
                )}
                <LobQueryRouter lobs={wizardLOBs} />
                <Viewport>
                  <ViewSmall>
                    <>
                      {isStorefrontPage && (
                        <CustomerNotifications location="AFTER_HEADER" className="customerNotificationSquare" />
                      )}
                      <TabbedWizardCard
                        wizardFormChildren={wizardFormChildren}
                        wizardState={wizardState}
                        wrappingClassNames="SimpleContainer mobileCardRadius"
                        wizardRegionType={WizardRegionType.COMET}
                      />
                    </>
                  </ViewSmall>
                  <ViewMedium>
                    <>
                      {isStorefrontPage && (
                        <CustomerNotifications
                          location="AFTER_HEADER"
                          className="customerNotificationSquare all-x-padding-six all-t-padding-twelve"
                        />
                      )}
                      <TabbedWizardCard
                        wizardFormChildren={wizardFormChildren}
                        wizardState={wizardState}
                        wrappingClassNames="all-t-padding-six all-x-padding-six SimpleContainer"
                        tabGroupClassNames="formAlignedTabs all-x-padding-six"
                        wizardFormClassNames="wizardOverHeroImage all-x-padding-six"
                        wizardRegionType={WizardRegionType.COMET}
                      />
                    </>
                  </ViewMedium>
                </Viewport>
              </UitkFigure>
              {otherFormChildren}
            </div>
          </div>
        </StorefrontWizardRegionTypesContext.Provider>
      </>
    );
  }
}

export default withStores(
  "wizardState",
  "compositionStore",
  "flexModuleModelStore",
  "context"
)(StorefrontWizardRegionCOMET);
